import {
  Box,
  Button,
  Card,
  CardHeader,
  Checkbox,
  CircularProgress,
  Collapse,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  makeStyles,
  MenuItem,
  OutlinedInput,
  Paper,
  PaperProps,
  Select,
  Switch,
  TextField,
  Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import SearchIcon from '@material-ui/icons/Search';
import { Alert, AlertTitle, Autocomplete } from '@material-ui/lab';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { EmptyView, SnackbarContext } from 'components';
import { CustomTooltip } from 'components/CustomTooltip';
import {
  getVacancyDefaults,
  getVacancyLookup,
  searchVacancyCandidates,
  submitVacancyCandidateFeedback,
  logResultClick,
} from 'lib/Api/SimilarCandidates';
import { getUserData } from 'lib/Helper/Auth';
import {
  AttachedCandidate,
  RatedBadCandidate,
  SimilarCandidateFeedbackAction,
  SimilarCandidateResult,
  SimilarCandidatesSearchFilters,
  VacancyCandidateFeedbackPayload,
  VacancyCandidatesTarget,
  VacancyDefaults,
  VacancyLookupItem,
} from 'lib/Model/SimilarCandidates';
import { toMonetary } from 'lib/Utility';
import * as React from 'react';
import { Feedback } from 'views/SimilarCandidates/components/FeedbackControls';
import { ResultCard } from 'views/SimilarCandidates/components/ResultCard';
import { ResultsList } from 'views/SimilarCandidates/components/ResultsList';
import {
  GuideStep,
  MatcherTutorial,
  referralSteps,
} from 'views/VacancyMatcher/MatcherGuide';
import { VacancySyncNote } from 'views/VacancyMatcher/VacancySyncNote';
import { TargetVacancyCard } from './components/TargetVacancyCard';

const permission = 'vacancy_matcher';
// the jim test user + superadmins, recruiters don't see the switch
const jimPermission = 'jim_mode';
// the jim tester, the page opens in jim mode for her every time
const jimTesterId = '107127837314369749240';
const maxResults = 20;
const focusMaxChars = 200;
// nothing reaching this rerank score = no close fit in the pool
const weakSetLlmThreshold = 0.7;
// jim mode = only candidates whose source metadata is this
const jimSource = 'jobsinmalta.com';
// an id or an ats url goes straight to the search, anything else searches vacancies by name
const vacancyIdPattern = /^\d+$|\/vacanc(y|ies)\/\d+/i;

type CountryFilter = 'malta' | 'eu' | 'world';

// the ats's own eu list (TcnTypes::$nonTcnCountryIds in talexio-recruit-api), eu-27 + iceland,
// liechtenstein, norway, switzerland. titles as in the ats countries table, which is what the index holds
const euCountries = [
  'Austria',
  'Belgium',
  'Bulgaria',
  'Croatia',
  'Cyprus',
  'Czech Republic',
  'Denmark',
  'Estonia',
  'Finland',
  'France',
  'Germany',
  'Greece',
  'Hungary',
  'Iceland',
  'Ireland',
  'Italy',
  'Latvia',
  'Liechtenstein',
  'Lithuania',
  'Luxembourg',
  'Malta',
  'Netherlands',
  'Norway',
  'Poland',
  'Portugal',
  'Romania',
  'Slovakia',
  'Slovenia',
  'Spain',
  'Sweden',
  'Switzerland',
];
type CvDateFilter = 'any' | '1' | '2' | '3';
type NliFilter = 'all' | 'nli' | 'non_nli';
// tcn_type_id: null = non-TCN, 1 = valid TCN, 2/3 = invalid/flagged. none ticked = all
type TcnOption = 'non_tcn' | 'valid' | 'invalid_flagged';
const tcnLabels: Record<TcnOption, string> = {
  non_tcn: 'Non-TCNs',
  valid: 'Valid TCNs',
  invalid_flagged: 'Invalid/Flagged',
};

const hexacoOptions = ['4', '5', '6', '7'];
const defaultHexacoFilter = '6';

interface FilterValues {
  countryFilter: CountryFilter;
  countryIncludeNull: boolean;
  cvDateFilter: CvDateFilter;
  nliFilter: NliFilter;
  tcnFilter: TcnOption[];
  hexacoFilter: string;
  hexacoIncludeNull: boolean;
  salaryMin: string;
  salaryMax: string;
  salaryIncludeNull: boolean;
}

// what the vacancy's own settings turn into, applied when a new vacancy is typed in
interface VacancyPrefill {
  tcnFilter: TcnOption[];
  salaryMin: string;
  salaryMax: string;
  salaryTop: number | null;
}

// what the pasted input resolves to, null when it isn't a vacancy we found
function vacancyLabel(defaults: VacancyDefaults): string | null {
  if (!defaults.vacancy_id) return null;
  return defaults.title || `Vacancy ${defaults.vacancy_id}`;
}

// the prefill note shows until the recruiter dismisses it once
const PREFILL_NOTE_KEY = 'vacancyMatcherPrefillNoteDismissed';

function toPrefill(defaults: VacancyDefaults): VacancyPrefill {
  let salaryMin = '';
  if (defaults.salary_min) {
    salaryMin = String(defaults.salary_min);
  }
  let salaryMax = '';
  if (defaults.salary_max) {
    salaryMax = String(defaults.salary_max);
  }
  let tcnFilter: TcnOption[] = [];
  if (defaults.tcn_filter === 'non_tcn') {
    tcnFilter = ['non_tcn'];
  }
  if (defaults.tcn_filter === 'non_tcn_or_valid') {
    tcnFilter = ['non_tcn', 'valid'];
  }
  return {
    tcnFilter,
    salaryMin,
    salaryMax,
    salaryTop: defaults.salary_top ?? null,
  };
}

type SearchState =
  | { kind: 'idle' }
  | { kind: 'loading'; notIndexed?: boolean }
  | { kind: 'error'; message: string; help?: string; code?: string }
  | {
      kind: 'loaded';
      searchId: string;
      vacancy: VacancyCandidatesTarget;
      results: SimilarCandidateResult[];
      candidatesConsidered?: number;
      rerankError?: string | null;
      alreadyAttached: AttachedCandidate[];
      hiddenRatedBad: RatedBadCandidate[];
    };

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(2),
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flexWrap: 'wrap',
  },
  field: {
    flex: 1,
    minWidth: 280,
  },
  searchButton: {
    height: 40,
  },
  // one line on a wide screen, fields top-aligned so the checkboxes hang under their own field
  filters: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, minmax(0, 1fr)) minmax(0, 1.6fr)',
    gap: theme.spacing(1.5),
    alignItems: 'start',
    marginTop: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    },
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  },
  filterCell: {
    minWidth: 0,
  },
  salaryRange: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  salaryField: {
    flex: '1 1 0',
    minWidth: 0,
    // no spinners, the range is typed
    '& input[type=number]': {
      MozAppearance: 'textfield',
    },
    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
      {
        WebkitAppearance: 'none',
        margin: 0,
      },
  },
  includeNull: {
    alignItems: 'flex-start',
    margin: theme.spacing(0.5, 0, 0),
    '& .MuiCheckbox-root': {
      padding: theme.spacing(0.25, 0.75, 0.25, 0),
    },
    '& .MuiFormControlLabel-label': {
      fontSize: 12,
      lineHeight: 1.4,
      color: theme.palette.text.secondary,
    },
  },
  filterNotes: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
    flexWrap: 'wrap',
    marginTop: theme.spacing(1),
  },
  zone: {
    marginTop: theme.spacing(3),
  },
  sectionHeader: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 1,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  loadingRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  // same grid as the results list
  hiddenCards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(2),
    alignItems: 'stretch',
    marginTop: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
}));

// the API sends { message, help, code } for known errors, a string otherwise
function getSearchError(error: unknown): {
  message: string;
  help?: string;
  code?: string;
} {
  const response = (
    error as { response?: { status?: number; data?: { detail?: unknown } } }
  )?.response;
  const detail = response?.data?.detail as
    | { message?: unknown; help?: unknown; code?: unknown }
    | string
    | undefined;

  if (
    detail &&
    typeof detail === 'object' &&
    typeof detail.message === 'string'
  ) {
    const known: { message: string; help?: string; code?: string } = {
      message: detail.message,
    };
    if (typeof detail.help === 'string') {
      known.help = detail.help;
    }
    if (typeof detail.code === 'string') {
      known.code = detail.code;
    }
    return known;
  }
  if (typeof detail === 'string' && detail.trim() !== '') {
    return { message: detail };
  }
  let status = '';
  if (response?.status) {
    status = ` (${response.status})`;
  }
  return {
    message: `Search failed${status}. Try again later.`,
  };
}

function buildFilters(f: FilterValues): SimilarCandidatesSearchFilters {
  const filters: SimilarCandidatesSearchFilters = {};
  if (f.countryFilter !== 'world') {
    const countries: Array<string | null> = [];
    if (f.countryFilter === 'malta') {
      countries.push('Malta');
    }
    if (f.countryFilter === 'eu') {
      countries.push(...euCountries);
    }
    if (f.countryIncludeNull) {
      countries.push(null);
    }
    filters.countries = countries;
  }
  if (f.cvDateFilter !== 'any') {
    filters.cv_date_within_years = Number(f.cvDateFilter);
  }
  if (f.nliFilter === 'nli') {
    filters.is_nli = true;
  }
  if (f.nliFilter === 'non_nli') {
    filters.is_nli = false;
  }
  if (f.tcnFilter.length > 0) {
    const tcnTypeIds: Array<number | null> = [];
    if (f.tcnFilter.includes('non_tcn')) {
      tcnTypeIds.push(null);
    }
    if (f.tcnFilter.includes('valid')) {
      tcnTypeIds.push(1);
    }
    if (f.tcnFilter.includes('invalid_flagged')) {
      tcnTypeIds.push(2, 3);
    }
    filters.tcn_type_ids = tcnTypeIds;
  }
  if (f.hexacoFilter !== '') {
    filters.hexaco_min = Number(f.hexacoFilter);
    filters.hexaco_include_null = f.hexacoIncludeNull;
  }
  const salaryMin = f.salaryMin.trim();
  const salaryMax = f.salaryMax.trim();
  if (salaryMin !== '') {
    filters.salary_min = Number(salaryMin);
  }
  if (salaryMax !== '') {
    filters.salary_max = Number(salaryMax);
  }
  if (salaryMin !== '' || salaryMax !== '') {
    filters.salary_include_null = f.salaryIncludeNull;
  }
  return filters;
}

interface Props {
  // set by the matcher page right after its intro closes
  startGuide?: boolean;
  onGuideStarted?(): void;
}

const VacancyCandidatesView: React.FC<Props> = ({
  startGuide,
  onGuideStarted,
}) => {
  const classes = useStyles();
  const snackbar = React.useContext(SnackbarContext);
  const [input, setInput] = React.useState('');
  const [lookupOptions, setLookupOptions] = React.useState<VacancyLookupItem[]>(
    [],
  );
  const [lookupLoading, setLookupLoading] = React.useState(false);
  const [includeClosed, setIncludeClosed] = React.useState(false);
  const [focusText, setFocusText] = React.useState('');
  const [countryFilter, setCountryFilter] =
    React.useState<CountryFilter>('malta');
  const [countryIncludeNull, setCountryIncludeNull] = React.useState(true);
  const [cvDateFilter, setCvDateFilter] = React.useState<CvDateFilter>('2');
  const [nliFilter, setNliFilter] = React.useState<NliFilter>('non_nli');
  const [includeAdjacent, setIncludeAdjacent] = React.useState(true);
  const [tcnFilter, setTcnFilter] = React.useState<TcnOption[]>([]);
  const [hexacoFilter, setHexacoFilter] = React.useState(defaultHexacoFilter);
  const [hexacoIncludeNull, setHexacoIncludeNull] = React.useState(true);
  const [salaryMin, setSalaryMin] = React.useState('');
  const [salaryMax, setSalaryMax] = React.useState('');
  const [salaryIncludeNull, setSalaryIncludeNull] = React.useState(true);
  const [jimMode, setJimMode] = React.useState(
    getUserData()?.id === jimTesterId,
  );
  // the input the prefill was last applied for, so typing the same vacancy again keeps the recruiter's edits
  const prefilledFor = React.useRef('');
  // who the pasted input resolves to, so the recruiter knows it's the right vacancy
  const [inputMatch, setInputMatch] = React.useState<{
    input: string;
    label: string | null;
  } | null>(null);
  const [lastPrefill, setLastPrefill] = React.useState<VacancyPrefill | null>(
    null,
  );
  const [prefillNoteDismissed, setPrefillNoteDismissed] = React.useState(
    () => localStorage.getItem(PREFILL_NOTE_KEY) === 'true',
  );
  const [search, setSearch] = React.useState<SearchState>({ kind: 'idle' });
  const [showRatedBad, setShowRatedBad] = React.useState(false);
  const [feedback, setFeedback] = React.useState<
    Record<string, Feedback | undefined>
  >({});
  const requestId = React.useRef(0);
  const [guideOpen, setGuideOpen] = React.useState(false);
  const searchRef = React.useRef<HTMLDivElement>(null);
  const focusRef = React.useRef<HTMLDivElement>(null);
  const filtersRef = React.useRef<HTMLDivElement>(null);
  const resultsRef = React.useRef<HTMLDivElement>(null);
  // memo so the guide doesn't re-scroll every time this view re-renders
  const guideSteps = React.useMemo<GuideStep[]>(
    () => [
      {
        title: 'Start with a vacancy',
        body: 'Search by job title, client or reference, or paste an ATS vacancy URL or ID. A vacancy new to the matcher is read on the spot, so its first search takes a little longer.',
        target: searchRef,
      },
      {
        // most bad ratings so far were about things the ad never said
        title: "Say what the job description doesn't",
        body: 'The ranking only knows what the vacancy says. If you want something it leaves out, like a technical background or a Maltese speaker, type it in Focus on.',
        target: focusRef,
      },
      {
        title: 'Check the filters',
        body: 'Max salary and TCN type are filled in from the vacancy. Change any filter before you search.',
        target: filtersRef,
      },
      {
        title: 'Read the results',
        body: "Each candidate has a match score and a short reason. If someone you'd expect is missing, flag them to Nick. Mark matches good or bad as you review them.",
        target: resultsRef,
      },
    ],
    [],
  );

  const hasPermission = getUserData()?.permissions?.includes(permission);
  const canUseJimMode = getUserData()?.permissions?.includes(jimPermission);

  React.useEffect(() => {
    if (!startGuide) return;
    setGuideOpen(true);
    onGuideStarted?.();
  }, [startGuide, onGuideStarted]);

  function applyPrefill(prefill: VacancyPrefill) {
    setLastPrefill(prefill);
    setTcnFilter(prefill.tcnFilter);
    setSalaryMin(prefill.salaryMin);
    setSalaryMax(prefill.salaryMax);
    setSalaryIncludeNull(true);
  }

  // prefill tcn + salary from the vacancy as the recruiter types
  React.useEffect(() => {
    const vacancy = input.trim();
    if (!vacancyIdPattern.test(vacancy) || prefilledFor.current === vacancy)
      return undefined;

    let cancelled = false;
    const handle = window.setTimeout(() => {
      getVacancyDefaults(vacancy)
        .then((defaults) => {
          if (cancelled) return;
          setInputMatch({ input: vacancy, label: vacancyLabel(defaults) });
          if (prefilledFor.current === vacancy) return;
          prefilledFor.current = vacancy;
          applyPrefill(toPrefill(defaults));
        })
        .catch(() => undefined);
    }, 500);

    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [input]);

  // the vacancy dropdown, empty box = what they searched lately
  React.useEffect(() => {
    const q = input.trim();
    if (vacancyIdPattern.test(q)) {
      setLookupOptions([]);
      return undefined;
    }

    let cancelled = false;
    let delay = 0;
    if (q) delay = 300;
    const handle = window.setTimeout(() => {
      setLookupLoading(true);
      getVacancyLookup(q, includeClosed)
        .then((rows) => {
          if (!cancelled) setLookupOptions(rows);
        })
        .catch(() => {
          if (!cancelled) setLookupOptions([]);
        })
        .finally(() => {
          if (!cancelled) setLookupLoading(false);
        });
    }, delay);

    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [input, includeClosed]);

  // the closed toggle sits under the list, mousedown kept off so the dropdown stays open
  const hasLookupQuery = !!input.trim();
  const LookupPaper = React.useCallback(
    (props: PaperProps) => (
      <Paper {...props}>
        {props.children}
        {hasLookupQuery && (
          <Box
            px={2}
            py={1}
            borderTop="1px solid rgba(0, 0, 0, 0.12)"
            onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
          >
            <Button
              size="small"
              color="primary"
              onClick={() => setIncludeClosed((v) => !v)}
            >
              {includeClosed
                ? 'Hide closed vacancies'
                : 'Show closed vacancies'}
            </Button>
          </Box>
        )}
      </Paper>
    ),
    [hasLookupQuery, includeClosed],
  );

  async function runSearch(
    force = false,
    processNow = false,
    notIndexed = false,
  ) {
    const vacancy = input.trim();
    if (!vacancyIdPattern.test(vacancy)) return;

    let filterValues: FilterValues = {
      countryFilter,
      countryIncludeNull,
      cvDateFilter,
      nliFilter,
      tcnFilter,
      hexacoFilter,
      hexacoIncludeNull,
      salaryMin,
      salaryMax,
      salaryIncludeNull,
    };
    // searched before the debounced prefill landed - fetch it now so the vacancy's tcn rule is never skipped
    if (prefilledFor.current !== vacancy) {
      try {
        const defaults = await getVacancyDefaults(vacancy);
        setInputMatch({ input: vacancy, label: vacancyLabel(defaults) });
        const prefill = toPrefill(defaults);
        prefilledFor.current = vacancy;
        applyPrefill(prefill);
        filterValues = {
          ...filterValues,
          tcnFilter: prefill.tcnFilter,
          salaryMin: prefill.salaryMin,
          salaryMax: prefill.salaryMax,
          salaryIncludeNull: true,
        };
      } catch {
        // the search itself will report a bad vacancy
      }
    }

    const minNum = Number(filterValues.salaryMin);
    const maxNum = Number(filterValues.salaryMax);
    if (
      filterValues.salaryMin.trim() !== '' &&
      filterValues.salaryMax.trim() !== '' &&
      minNum > maxNum
    ) {
      setSearch({
        kind: 'error',
        message: 'Min salary cannot be greater than Max salary.',
        help: 'Clear one salary field or enter a valid salary range.',
      });
      return;
    }

    const activeRequest = requestId.current + 1;
    requestId.current = activeRequest;
    setFeedback({});
    setSearch({ kind: 'loading', notIndexed });

    try {
      const filters = buildFilters(filterValues);
      if (force) {
        filters.force = true;
      }
      if (focusText.trim()) {
        filters.focus = focusText.trim();
      }
      filters.include_adjacent = includeAdjacent;
      if (processNow) {
        filters.process_now = true;
      }
      if (jimMode) {
        filters.candidate_source = jimSource;
      }
      const response = await searchVacancyCandidates(vacancy, filters);
      if (requestId.current !== activeRequest) return;

      const results = [...response.results]
        .sort((a, b) => b.final_score - a.final_score)
        .slice(0, maxResults);
      setSearch({
        kind: 'loaded',
        searchId: response.search_id,
        vacancy: response.target,
        results,
        candidatesConsidered: response.candidates_considered,
        rerankError: response.rerank_error,
        alreadyAttached: response.already_attached ?? [],
        hiddenRatedBad: response.hidden_rated_bad ?? [],
      });
      // the hidden ones come back already marked bad, undo on the card brings them back next search
      const marked: Record<string, Feedback | undefined> = {};
      for (const hidden of response.hidden_rated_bad ?? []) {
        marked[hidden.candidate_id] = {
          kind: 'bad',
          reasons: hidden.reasons,
          note: hidden.note ?? undefined,
        };
      }
      setFeedback(marked);
    } catch (error) {
      if (requestId.current !== activeRequest) return;
      const searchError = getSearchError(error);
      // not in the index yet: process it straight away instead of asking
      if (searchError.code === 'vacancy_not_indexed' && !processNow) {
        runSearch(force, true, true);
        return;
      }
      setSearch({ kind: 'error', ...searchError });
    }
  }

  function dismissPrefillNote() {
    localStorage.setItem(PREFILL_NOTE_KEY, 'true');
    setPrefillNoteDismissed(true);
  }

  function handleResultOpen(id: string) {
    if (search.kind !== 'loaded') return;
    const rank = search.results.findIndex((r) => r.candidate_id === id) + 1;
    // a hidden rated-bad one has no rank, so no click log, but the copy still helps the referral
    if (rank > 0) {
      logResultClick('vacancy_click', {
        search_id: search.searchId,
        result_id: id,
        rank,
      });
    }

    // the recruiter pastes it into the ats to find the vacancy for the referral
    const reference = search.vacancy.reference;
    if (!reference) return;
    navigator.clipboard
      .writeText(reference)
      .then(() => snackbar.success(`${reference} copied. ${referralSteps}`))
      .catch(() =>
        snackbar.error(`Couldn't copy ${reference}. Please copy it manually.`),
      );
  }

  function handleFeedbackChange(id: string, next: Feedback | undefined) {
    setFeedback((prev) => ({ ...prev, [id]: next }));
    if (search.kind !== 'loaded') return;

    let rank: number | null =
      search.results.findIndex((r) => r.candidate_id === id) + 1;
    let result: SimilarCandidateResult | undefined = search.results[rank - 1];
    // or one of the hidden rated-bad ones, unranked
    if (!result) {
      rank = null;
      result = search.hiddenRatedBad.find((r) => r.candidate_id === id);
    }
    if (!result) return;
    let action: SimilarCandidateFeedbackAction = 'cleared';
    if (next !== undefined) {
      action = next.kind;
    }
    const payload: VacancyCandidateFeedbackPayload = {
      search_id: search.searchId,
      action,
      result,
      vacancy_id: search.vacancy.vacancy_id,
      rank,
    };
    if (next?.kind === 'bad') {
      payload.reasons = next.reasons;
      payload.note = next.note;
    }
    // best effort, never get in the recruiter's way
    submitVacancyCandidateFeedback(payload).catch(() => undefined);
  }

  if (!hasPermission) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
      >
        <Typography variant="h6" color="error">
          You do not have permission to access this feature.
        </Typography>
      </Box>
    );
  }

  const rerankRan =
    search.kind === 'loaded' && search.results.some((r) => r.llm_score != null);
  const rerankFailed =
    search.kind === 'loaded' && search.results.length > 0 && !rerankRan;
  const weakSet =
    search.kind === 'loaded' &&
    rerankRan &&
    !search.results.some((r) => (r.llm_score ?? 0) >= weakSetLlmThreshold);

  let inputHelper: string | undefined;
  let inputNotFound = false;
  if (inputMatch && inputMatch.input === input.trim()) {
    if (inputMatch.label) {
      inputHelper = `Vacancy: ${inputMatch.label}`;
    } else {
      inputHelper = 'No vacancy found for this input.';
      inputNotFound = true;
    }
  }

  let errorSeverity: 'warning' | 'info' = 'info';
  if (search.kind === 'error' && search.code === 'vacancy_profile_thin') {
    errorSeverity = 'warning';
  }

  let resultsSummary = '';
  if (search.kind === 'loaded') {
    resultsSummary = `Top ${search.results.length} matches, ranked best first`;
    if (typeof search.candidatesConsidered === 'number') {
      resultsSummary += ` - from ${search.candidatesConsidered.toLocaleString()} candidates that matched your filters.`;
    } else {
      resultsSummary += '.';
    }
  }

  return (
    <Grid container>
      <Grid item xs={12}>
        <Card className={classes.card}>
          <CardHeader
            title="Find Candidates"
            subheader={
              <>
                Find the candidates who best fit a vacancy.
                <VacancySyncNote />
              </>
            }
            action={
              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                {canUseJimMode && (
                  <FormControlLabel
                    control={
                      <Switch
                        color="primary"
                        checked={jimMode}
                        onChange={(e) => setJimMode(e.target.checked)}
                      />
                    }
                    label={
                      <CustomTooltip
                        titleHeader="JIM Mode"
                        bodyText="Only searches candidates who came in from jobsinmalta.com."
                        placement="bottom"
                        maxWidth={320}
                        useDefaultUnderline
                      >
                        JIM Mode
                      </CustomTooltip>
                    }
                  />
                )}
                <Button
                  onClick={() => setGuideOpen(true)}
                  variant="outlined"
                  size="small"
                  startIcon={<HelpOutlineIcon />}
                >
                  Show guide
                </Button>
              </Box>
            }
          />

          <Box px={2}>
            {/* top, not centre: the name under the input would push the button down */}
            <div
              ref={searchRef}
              className={classes.row}
              style={{ alignItems: 'flex-start' }}
            >
              <Autocomplete<VacancyLookupItem, false, false, true>
                className={classes.field}
                freeSolo
                openOnFocus
                autoHighlight
                options={lookupOptions}
                loading={lookupLoading}
                // the server already filtered, don't let mui filter again on the label
                filterOptions={(options) => options}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  return option.vacancy_id;
                }}
                groupBy={
                  hasLookupQuery ? undefined : () => 'Your recent searches'
                }
                value={null}
                inputValue={input}
                onInputChange={(e, value, reason) => {
                  // mui resets the text after a pick, we set it ourselves in onChange
                  if (reason === 'reset') return;
                  setInput(value);
                }}
                onChange={(e, picked) => {
                  if (picked && typeof picked !== 'string') {
                    setInput(picked.vacancy_id);
                  }
                }}
                PaperComponent={LookupPaper}
                renderOption={(option) => {
                  const details = [];
                  if (option.company) details.push(option.company);
                  if (option.reference) details.push(option.reference);
                  if (option.last_searched) {
                    details.push(
                      `searched ${formatDistanceToNow(
                        parseISO(option.last_searched),
                      )} ago`,
                    );
                  } else if (option.modified) {
                    details.push(
                      `updated ${formatDistanceToNow(
                        parseISO(option.modified),
                      )} ago`,
                    );
                  }
                  return (
                    <Box width="100%">
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        style={{ gap: 8 }}
                      >
                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                          {option.title || `Vacancy ${option.vacancy_id}`}
                        </Typography>
                        <Typography
                          variant="caption"
                          color={option.is_closed ? 'error' : 'textSecondary'}
                        >
                          {option.vacancy_status}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="textSecondary">
                        {details.join(' · ')}
                      </Typography>
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    label="Vacancy"
                    placeholder="Search by job title, client or reference, or paste an ATS URL or ID"
                    InputLabelProps={{
                      ...params.InputLabelProps,
                      shrink: true,
                    }}
                    helperText={inputHelper}
                    error={inputNotFound}
                    onKeyDown={(e) => {
                      // words pick from the dropdown instead
                      if (
                        e.key === 'Enter' &&
                        vacancyIdPattern.test(input.trim())
                      )
                        runSearch();
                    }}
                  />
                )}
              />
              <Button
                className={classes.searchButton}
                variant="contained"
                color="primary"
                startIcon={<SearchIcon />}
                disabled={
                  search.kind === 'loading' ||
                  !vacancyIdPattern.test(input.trim())
                }
                onClick={() => runSearch()}
              >
                Search
              </Button>
            </div>
            <div ref={focusRef}>
              <Box className={classes.row} mt={1}>
                <TextField
                  className={classes.field}
                  variant="outlined"
                  size="small"
                  label="Focus on… (optional)"
                  placeholder='e.g. "must have retail experience"'
                  InputLabelProps={{ shrink: true }}
                  value={focusText}
                  onChange={(e) =>
                    setFocusText(e.currentTarget.value.slice(0, focusMaxChars))
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') runSearch();
                  }}
                  helperText="Tell the ranking what matters most."
                />
              </Box>
            </div>

            <div ref={filtersRef}>
              <Box className={classes.filters}>
                <Box className={classes.filterCell}>
                  <FormControl variant="outlined" size="small" fullWidth>
                    <InputLabel id="vacancy-country-filter">Country</InputLabel>
                    <Select
                      labelId="vacancy-country-filter"
                      label="Country"
                      value={countryFilter}
                      onChange={(e) =>
                        setCountryFilter(e.target.value as CountryFilter)
                      }
                    >
                      <MenuItem value="malta">Malta only</MenuItem>
                      <MenuItem value="eu">Anyone in the EU</MenuItem>
                      <MenuItem value="world">Anywhere in the world</MenuItem>
                    </Select>
                  </FormControl>
                  {/* anywhere already keeps everyone, missing country included */}
                  <FormControlLabel
                    className={classes.includeNull}
                    control={
                      <Checkbox
                        size="small"
                        color="primary"
                        checked={countryIncludeNull}
                        disabled={countryFilter === 'world'}
                        onChange={(e) =>
                          setCountryIncludeNull(e.target.checked)
                        }
                      />
                    }
                    label={
                      <CustomTooltip
                        titleHeader="Allow missing country"
                        bodyText="Keeps candidates whose profile and CV don't say where they live. Many locals have no country on their profile yet, so unticking this can drop them. Some of these people may be based abroad."
                        placement="bottom"
                        maxWidth={320}
                        useDefaultUnderline
                      >
                        Allow missing country
                      </CustomTooltip>
                    }
                  />
                  {countryFilter === 'malta' && (
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      component="div"
                      style={{ marginTop: 4, lineHeight: 1.4 }}
                    >
                      {countryIncludeNull && (
                        <strong>
                          Includes people without country information.{' '}
                        </strong>
                      )}
                      This means people who live in Malta, not Maltese
                      nationals.
                    </Typography>
                  )}
                  {countryFilter === 'eu' && (
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      component="div"
                      style={{ marginTop: 4, lineHeight: 1.4 }}
                    >
                      EU countries plus Iceland, Liechtenstein, Norway and
                      Switzerland, same as the ATS. This is where the candidate
                      is, not their nationality.
                    </Typography>
                  )}
                </Box>
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel id="vacancy-cv-date-filter">CV date</InputLabel>
                  <Select
                    labelId="vacancy-cv-date-filter"
                    label="CV date"
                    value={cvDateFilter}
                    onChange={(e) =>
                      setCvDateFilter(e.target.value as CvDateFilter)
                    }
                  >
                    <MenuItem value="1">Within the last year</MenuItem>
                    <MenuItem value="2">Within 2 years</MenuItem>
                    <MenuItem value="3">Within 3 years</MenuItem>
                    {/* no cutoff, so older cvs aren't dropped */}
                    <MenuItem value="any">
                      Any date (incl. older than 3 years)
                    </MenuItem>
                  </Select>
                </FormControl>
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel id="vacancy-nli-filter">NLI status</InputLabel>
                  <Select
                    labelId="vacancy-nli-filter"
                    label="NLI status"
                    value={nliFilter}
                    onChange={(e) => setNliFilter(e.target.value as NliFilter)}
                  >
                    <MenuItem value="all">All candidates</MenuItem>
                    <MenuItem value="nli">NLI only</MenuItem>
                    <MenuItem value="non_nli">Non-NLI only</MenuItem>
                  </Select>
                </FormControl>
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel id="vacancy-tcn-filter" shrink>
                    TCN type
                  </InputLabel>
                  <Select
                    labelId="vacancy-tcn-filter"
                    input={<OutlinedInput notched label="TCN type" />}
                    multiple
                    displayEmpty
                    value={tcnFilter}
                    onChange={(e) =>
                      setTcnFilter(e.target.value as TcnOption[])
                    }
                    renderValue={(selected) => {
                      const options = selected as TcnOption[];
                      if (options.length === 0) return 'All candidates';
                      return options.map((o) => tcnLabels[o]).join(', ');
                    }}
                  >
                    {(Object.keys(tcnLabels) as TcnOption[]).map((option) => (
                      <MenuItem key={option} value={option}>
                        <Checkbox
                          size="small"
                          checked={tcnFilter.includes(option)}
                        />
                        {tcnLabels[option]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box className={classes.filterCell}>
                  <FormControl variant="outlined" size="small" fullWidth>
                    <InputLabel id="vacancy-hexaco-filter">
                      Min HEXACO
                    </InputLabel>
                    <Select
                      labelId="vacancy-hexaco-filter"
                      label="Min HEXACO"
                      value={hexacoFilter}
                      onChange={(e) =>
                        setHexacoFilter(e.target.value as string)
                      }
                    >
                      <MenuItem value="">Any</MenuItem>
                      {hexacoOptions.map((value) => (
                        <MenuItem key={value} value={value}>
                          {`${value} or greater`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControlLabel
                    className={classes.includeNull}
                    control={
                      <Checkbox
                        size="small"
                        color="primary"
                        checked={hexacoIncludeNull}
                        disabled={hexacoFilter === ''}
                        onChange={(e) => setHexacoIncludeNull(e.target.checked)}
                      />
                    }
                    label={
                      <CustomTooltip
                        titleHeader="Allow missing HEXACO"
                        bodyText="Keeps candidates with no HEXACO score in the search. They are often people we have not met yet, so this can surface new candidates. Untick it when the role needs a HEXACO score above the selected minimum."
                        placement="bottom"
                        maxWidth={320}
                        useDefaultUnderline
                      >
                        Allow missing HEXACO
                      </CustomTooltip>
                    }
                  />
                </Box>
                <Box className={classes.filterCell}>
                  <Box className={classes.salaryRange}>
                    <TextField
                      className={classes.salaryField}
                      variant="outlined"
                      size="small"
                      type="number"
                      label="Min salary"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: 0 }}
                      value={salaryMin}
                      onChange={(e) => setSalaryMin(e.target.value)}
                    />
                    <TextField
                      className={classes.salaryField}
                      variant="outlined"
                      size="small"
                      type="number"
                      label="Max salary"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: 0 }}
                      value={salaryMax}
                      onChange={(e) => setSalaryMax(e.target.value)}
                    />
                  </Box>
                  <FormControlLabel
                    className={classes.includeNull}
                    control={
                      <Checkbox
                        size="small"
                        color="primary"
                        checked={salaryIncludeNull}
                        disabled={
                          salaryMin.trim() === '' && salaryMax.trim() === ''
                        }
                        onChange={(e) => setSalaryIncludeNull(e.target.checked)}
                      />
                    }
                    label={
                      <CustomTooltip
                        titleHeader="Include candidates without salary on profile"
                        bodyText="Many candidates have no salary on their profile. Ticking this keeps them in the search, which widens the pool of possible candidates a lot. Untick it to see only candidates whose salary is within the range."
                        placement="bottom"
                        maxWidth={320}
                        useDefaultUnderline
                      >
                        Include candidates without salary on profile
                      </CustomTooltip>
                    }
                  />
                </Box>
              </Box>
              <Box className={classes.filterNotes}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      color="primary"
                      checked={includeAdjacent}
                      onChange={(e) => setIncludeAdjacent(e.target.checked)}
                    />
                  }
                  label={
                    <CustomTooltip
                      titleHeader="Include people from related roles"
                      bodyText="Also looks for people who don't do this exact job but come from a closely related one, and could step into it. For example, a customer service agent who handles orders, for a sales admin vacancy. It finds more candidates, but the search takes a bit longer. Untick it to see only people already doing this kind of job."
                      placement="bottom"
                      maxWidth={320}
                      useDefaultUnderline
                    >
                      Include people from related roles (slower)
                    </CustomTooltip>
                  }
                />
              </Box>
            </div>
            {lastPrefill &&
              !prefillNoteDismissed &&
              (lastPrefill.salaryMax !== '' ||
                lastPrefill.tcnFilter.length > 0) && (
                <Alert
                  severity="info"
                  className={classes.zone}
                  onClose={dismissPrefillNote}
                >
                  <AlertTitle>
                    Some filters were filled in from this vacancy
                  </AlertTitle>
                  {lastPrefill.tcnFilter.length > 0 && (
                    <Typography variant="body2">
                      TCN type follows the vacancy's work eligibility rule.
                    </Typography>
                  )}
                  {lastPrefill.salaryMax !== '' &&
                    lastPrefill.salaryTop != null && (
                      <Typography variant="body2">
                        Max salary is the top of the vacancy's pay,{' '}
                        {toMonetary(lastPrefill.salaryTop)}, plus a leeway of{' '}
                        {toMonetary(
                          Number(lastPrefill.salaryMax) - lastPrefill.salaryTop,
                        )}
                        : 10% or {toMonetary(5000)}, whichever is larger. So
                        candidates who want a little more still appear. Min
                        salary stays empty, because someone earning less may
                        welcome the step up.
                      </Typography>
                    )}
                  <Typography variant="body2" style={{ marginTop: 6 }}>
                    You can change these filters before you search.
                  </Typography>
                </Alert>
              )}

            {search.kind === 'idle' && (
              <EmptyView>
                Paste a vacancy URL or enter a vacancy ID to begin.
              </EmptyView>
            )}

            {search.kind === 'loading' && (
              <Box className={`${classes.zone} ${classes.loadingRow}`}>
                <CircularProgress size={18} />
                <Typography variant="body2">
                  {search.notIndexed
                    ? 'This vacancy is new to the matcher, so reading it first. The search takes a bit longer this time…'
                    : 'Reading the vacancy and finding the best fits…'}
                </Typography>
              </Box>
            )}

            {search.kind === 'error' && (
              <Alert severity={errorSeverity} className={classes.zone}>
                <span>{search.message}</span>
                {search.help && (
                  <Typography variant="body2" style={{ marginTop: 6 }}>
                    {search.help}
                  </Typography>
                )}
                {search.code === 'vacancy_profile_thin' && (
                  <Box mt={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      onClick={() => runSearch(true, true)}
                    >
                      Search anyway
                    </Button>
                  </Box>
                )}
              </Alert>
            )}

            {search.kind === 'loaded' && (
              <>
                <Box className={classes.zone}>
                  <TargetVacancyCard vacancy={search.vacancy} />
                </Box>

                <div ref={resultsRef} className={classes.zone}>
                  <Typography className={classes.sectionHeader}>
                    Best fits
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {resultsSummary}
                  </Typography>

                  {search.alreadyAttached.length > 0 && (
                    <Typography variant="body2" color="textSecondary">
                      Excluded {search.alreadyAttached.length}{' '}
                      {search.alreadyAttached.length === 1
                        ? 'candidate'
                        : 'candidates'}{' '}
                      who already applied
                    </Typography>
                  )}
                  {search.hiddenRatedBad.length > 0 && (
                    <Box mt={0.5}>
                      <Button
                        size="small"
                        color="primary"
                        onClick={() => setShowRatedBad((x) => !x)}
                        endIcon={
                          <ExpandMoreIcon
                            style={{
                              transform: showRatedBad
                                ? 'rotate(180deg)'
                                : undefined,
                            }}
                          />
                        }
                      >
                        Excluded {search.hiddenRatedBad.length}{' '}
                        {search.hiddenRatedBad.length === 1
                          ? 'candidate'
                          : 'candidates'}{' '}
                        you rated badly
                      </Button>
                      <Collapse in={showRatedBad}>
                        <Typography variant="body2" color="textSecondary">
                          Left out of the ranking. Undo the rating on a card to
                          bring it back in your next search.
                        </Typography>
                        <Box className={classes.hiddenCards}>
                          {search.hiddenRatedBad.map((result, i) => (
                            <ResultCard
                              key={result.candidate_id}
                              rank={i + 1}
                              isStrong={false}
                              result={result}
                              finalScore={result.final_score}
                              feedback={feedback[result.candidate_id]}
                              onFeedbackChange={(next) =>
                                handleFeedbackChange(result.candidate_id, next)
                              }
                              onOpen={() =>
                                handleResultOpen(result.candidate_id)
                              }
                            />
                          ))}
                        </Box>
                      </Collapse>
                    </Box>
                  )}

                  {rerankFailed && (
                    <Alert severity="error" style={{ marginTop: 8 }}>
                      Smart ranking failed for this search, so the order below
                      is approximate and match explanations are missing.
                      Re-running the search usually fixes this.
                      {search.rerankError && (
                        <Typography
                          variant="caption"
                          style={{ display: 'block', fontFamily: 'monospace' }}
                        >
                          {search.rerankError}
                        </Typography>
                      )}
                    </Alert>
                  )}
                  {weakSet && (
                    <Alert severity="warning" style={{ marginTop: 8 }}>
                      <strong>No strong fits found.</strong> These are the
                      closest we have. Treat them as leads to review.
                    </Alert>
                  )}

                  {search.results.length === 0 && (
                    <EmptyView>No candidates matched your filters.</EmptyView>
                  )}
                  {search.results.length > 0 && (
                    <Box mt={2}>
                      <ResultsList
                        results={search.results}
                        feedback={feedback}
                        onFeedbackChange={handleFeedbackChange}
                        onOpen={handleResultOpen}
                        hideWeak
                      />
                    </Box>
                  )}
                </div>
              </>
            )}
          </Box>
        </Card>
      </Grid>
      <MatcherTutorial
        steps={guideSteps}
        open={guideOpen}
        onClose={() => setGuideOpen(false)}
      />
    </Grid>
  );
};

export default VacancyCandidatesView;
